import React, { useState, useEffect } from "react";
import { Sparkles, Hammer, AlertTriangle, Info, Megaphone, X, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

export interface MaintenanceBannerData {
  id: string;
  title: string;
  message: string;
  banner_type: 'maintenance' | 'warning' | 'info' | 'announcement';
  is_active: boolean;
  is_dismissible: boolean;
  action_text?: string | null;
  action_url?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}

const MaintenanceBanner = () => {
  const [activeBanner, setActiveBanner] = useState<MaintenanceBannerData | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem("dismissed_banners_sara");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const fetchActiveBanner = async () => {
    try {
      const { data, error } = await supabase
        .from("maintenance_banners")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        // If table does not exist yet or error occurs, fallback silently
        return;
      }

      if (data && data.length > 0) {
        const now = new Date().getTime();
        const validBanner = data.find((b) => {
          if (b.start_time && new Date(b.start_time).getTime() > now) return false;
          if (b.end_time && new Date(b.end_time).getTime() < now) return false;
          return true;
        });

        setActiveBanner(validBanner || null);
      } else {
        setActiveBanner(null);
      }
    } catch {
      // Silent catch
    }
  };

  useEffect(() => {
    fetchActiveBanner();

    // Subscribe to realtime updates for maintenance_banners
    const channel = supabase
      .channel("public:maintenance_banners")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "maintenance_banners" },
        () => {
          fetchActiveBanner();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => {
      const next = [...prev, id];
      try {
        sessionStorage.setItem("dismissed_banners_sara", JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  if (!activeBanner) return null;
  if (activeBanner.is_dismissible && dismissedIds.includes(activeBanner.id)) return null;

  const isMaintenance = activeBanner.banner_type === "maintenance";
  const isWarning = activeBanner.banner_type === "warning";
  const isInfo = activeBanner.banner_type === "info";
  const isAnnouncement = activeBanner.banner_type === "announcement";

  return (
    <div
      className={cn(
        "relative z-[100] border-b shadow-xs transition-all duration-300",
        isMaintenance && "bg-gradient-to-r from-[#D4AF37] via-[#F3E5AB] to-[#D4AF37] border-amber-300 text-slate-950",
        isWarning && "bg-gradient-to-r from-rose-600 via-amber-600 to-rose-600 text-white border-rose-700",
        isInfo && "bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 text-white border-blue-700",
        isAnnouncement && "bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 text-white border-emerald-700"
      )}
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-4">
        <div className="flex-1 flex items-center justify-center gap-3 min-w-0">
          <div className="flex items-center gap-2 shrink-0">
            <span className={cn("inline-block h-2 w-2 rounded-full shrink-0", isMaintenance ? "bg-slate-900" : "bg-white")}></span>

            {isMaintenance && <Sparkles className="h-4 w-4 text-slate-900 shrink-0" />}
            {isWarning && <AlertTriangle className="h-4 w-4 text-white shrink-0" />}
            {isInfo && <Info className="h-4 w-4 text-white shrink-0" />}
            {isAnnouncement && <Megaphone className="h-4 w-4 text-white shrink-0" />}
          </div>

          <p className="text-xs md:text-sm font-bold tracking-tight truncate">
            {activeBanner.title && (
              <span className="uppercase tracking-wider font-black mr-2 opacity-90">
                {activeBanner.title}:
              </span>
            )}
            <span>{activeBanner.message}</span>
          </p>

          {isMaintenance && (
            <Hammer className="h-4 w-4 text-slate-900 hidden sm:block shrink-0" />
          )}

          {activeBanner.action_text && activeBanner.action_url && (
            <a
              href={activeBanner.action_url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-extrabold uppercase tracking-wider shrink-0 transition-all hover:scale-105 shadow-2xs",
                isMaintenance ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white/20 hover:bg-white/30 text-white backdrop-blur-xs"
              )}
            >
              <span>{activeBanner.action_text}</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {activeBanner.is_dismissible && (
          <button
            onClick={() => handleDismiss(activeBanner.id)}
            className={cn(
              "p-1 rounded-lg transition-colors shrink-0",
              isMaintenance ? "hover:bg-slate-950/10 text-slate-800" : "hover:bg-white/20 text-white/80 hover:text-white"
            )}
            aria-label="Cerrar aviso"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MaintenanceBanner;
